import { environment } from '@energy-stacks/feature-config';
import { createBaseQuery } from '@energy-stacks/shared';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { JobModel, JobPageModel } from './jobModel';
import { jobNormalizer } from './normalizers/jobsNormalizer';
import { JobDto } from './jobDto';
import { GetJobsPoolRequestBody } from './getJobsPoolRequestBody';
import { getDataByPlantId, jobStatusMap } from '@energy-stacks/fleet/shared';
import {
  JobsTableBackendSortOrderModel,
  mapJobsTableSortIdToPayload,
} from './mapJobsTableSortIdToPayload';
import {
  OptimizedTourModel,
  toursApi,
} from '@energy-stacks/fleet/feature-tours-data';
import { paginatedJobsNormalizer } from './normalizers/paginatedJobsNormalizer';

const serializeGetJobPoolParameters = (
  searchParams: GetJobsPoolRequestBody
): string => {
  const params = new URLSearchParams();

  Object.entries(searchParams).forEach(([key, value]) => {
    if (!Array.isArray(value)) {
      params.append(key, value);
    } else {
      value.forEach((item) => params.append(key, item));
    }
  });

  return params.toString();
};

export type GetJobsRequestBody = {
  searchValue?: string;
  page: number;
  size: number;
  sort?: JobsTableBackendSortOrderModel;
  statuses?: string[];
  startDateTime?: string;
  endDateTime?: string;
  jobTypes?: string[];
};

export const jobsApi = createApi({
  reducerPath: 'jobs',
  tagTypes: ['Jobs', 'JobsPool', 'PaginatedJobs'],
  keepUnusedDataFor: 0,
  baseQuery: createBaseQuery(`${environment.ocppServiceUrl}/jobs`),
  endpoints: (builder) => ({
    getPaginatedJobs: builder.query<JobPageModel, GetJobsRequestBody>({
      queryFn: async (params, api, _extraArgs, baseQuery) => {
        return getDataByPlantId({
          api,
          baseQuery,
          url: '/search',
          transformResponse: paginatedJobsNormalizer,
          params: {
            ...params,
            sort: params?.sort
              ? mapJobsTableSortIdToPayload(params.sort)
              : undefined,
          },
        });
      },
      providesTags: ['PaginatedJobs'],
    }),
    getJobPool: builder.query<JobModel[], GetJobsPoolRequestBody>({
      query: (searchParams) => ({
        url: `/excluding?${serializeGetJobPoolParameters(searchParams)}`,
        method: 'GET',
      }),
      providesTags: ['JobsPool'],
      transformResponse: (jobsDto: JobDto[]) => {
        return jobsDto.map(jobNormalizer);
      },
    }),
    changeJobStatus: builder.mutation<
      void,
      {
        jobId: JobDto['jobUid'];
        status: JobDto['status'];
        tourId: OptimizedTourModel['id'];
      }
    >({
      query: ({ jobId, status }) => ({
        url: `/${jobId}/status`,
        method: 'PATCH',
        body: {
          jobStatus: status,
        },
      }),
      async onQueryStarted(
        { jobId, status, tourId },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(
            toursApi.util.updateQueryData('getTourDetails', tourId, (draft) => {
              for (const job of draft.tourJobs.jobs) {
                if (job.jobId === jobId) {
                  job.status = jobStatusMap[status];
                  break;
                }
              }
            })
          );
        } catch {}
      },
      invalidatesTags: ['Jobs', 'PaginatedJobs'],
    }),
  }),
});

export const {
  useGetJobPoolQuery,
  useChangeJobStatusMutation,
  useGetPaginatedJobsQuery,
  usePrefetch,
} = jobsApi;
